import { useRef, useState, type FormEvent } from "react";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@sylviq/ui-react-web/components/ui/card";
import { decideCliDevice, getCliDeviceReview, type CliDeviceReview } from "@/lib/api";
import { useSession } from "@/lib/session";

export function CliDeviceConsentPage() {
  const { state } = useSession();
  const [code, setCode] = useState("");
  const [review, setReview] = useState<CliDeviceReview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const inFlight = useRef(false);
  const load = async (event: FormEvent) => {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(""); setReview(null);
    try {
      const request = await getCliDeviceReview(code.trim().toUpperCase());
      if (request.client_name !== "SYLVIQ CLI" || !["build:read", "build:read build:write", "secrets:manage"].includes(request.scope)
          || request.user_code.replaceAll("-", "") !== code.trim().toUpperCase().replaceAll("-", "")
          || !Number.isFinite(request.expires_at) || request.expires_at * 1000 <= Date.now()) throw new Error("Unverified request");
      setReview(request);
    } catch {
      setError("This request could not be verified. Check the code on your terminal, your organization access, and whether it expired.");
    } finally { setBusy(false); inFlight.current = false; }
  };
  const decide = async (approve: boolean) => {
    if (!review || inFlight.current || review.expires_at * 1000 <= Date.now()) return;
    inFlight.current = true; setBusy(true); setError("");
    // Clear consent controls before the mutation: interrupted decisions must
    // not be automatically retried with stale review state.
    setReview(null);
    try {
      const decision = await decideCliDevice(review.user_code, approve);
      if (decision.status !== (approve ? "approved" : "denied")) throw new Error("Unconfirmed decision");
      setResult(approve ? "Access approved. Return to your terminal." : "Access denied.");
    } catch {
      setError("The decision was not confirmed. Sign in with authenticator verification, then check the request again. Do not approve codes sent by someone else.");
    } finally { setBusy(false); inFlight.current = false; }
  };
  return <main className="mx-auto w-full max-w-xl px-4 py-8">
    <Card><CardHeader><CardTitle><h1>Connect SYLVIQ CLI</h1></CardTitle></CardHeader><CardContent className="grid gap-4">
      <p>Only enter a code from a terminal you control. Never approve a code someone sent you. Access lasts 15 minutes and is limited to the reviewed organization.</p>
      {state.status === "loading" ? <p role="status">Checking sign-in…</p> : state.status !== "authenticated" ? <a href="/login?cli_device=1">Sign in to review access</a> : result ? <p role="status">{result}</p> : <>
        <form onSubmit={load} className="grid gap-3">
          <label htmlFor="cli-device-code">Code shown in your terminal</label>
          <Input id="cli-device-code" value={code} onChange={event => { setCode(event.target.value); setReview(null); }} autoComplete="off" autoCapitalize="characters" maxLength={11} required disabled={busy} />
          <Button type="submit" disabled={busy || !/^[A-Fa-f0-9]{5}-?[A-Fa-f0-9]{5}$/.test(code.trim())}>{busy ? "Checking…" : "Review access"}</Button>
        </form>
        {review ? <section aria-label="Requested access" className="grid gap-3">
          <h2>SYLVIQ CLI requests access</h2>
          <p className="break-all">Organization: {review.organization_name}</p>
          <p>{review.scope === "secrets:manage" ? "List organization secret names and create, replace, or delete them. Values are entered only in your terminal. No extension receives a value." : review.scope.includes("build:write") ? "Read and edit extension drafts and binary fixtures." : "Read extension drafts and binary fixtures."} No publishing, installing, running extensions, or posting to your books.</p>
          <p>Confirm this code matches your terminal: {review.user_code}</p>
          <p>Authenticator verification is required to decide.</p>
          <div className="flex flex-wrap gap-3"><Button onClick={() => decide(true)} disabled={busy}>Allow access</Button><Button variant="secondary" onClick={() => decide(false)} disabled={busy}>Deny</Button></div>
        </section> : null}
      </>}
      {error ? <p role="alert">{error}</p> : null}
    </CardContent></Card>
  </main>;
}
